import { LockKeyOpenIcon } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/error';
import { toast } from '@/lib/toast';
import { unlockUserRequest } from '@/services/users';
import type { UserDetail } from '@/types/users';

/**
 * Plan 08-02 UB1-3: a single-button affordance that ONLY renders when
 *  - `user.lockedUntil` is in the future (`status === 'Locked'` semantically), AND
 *  - the caller holds `users.unlock`.
 *
 * On click → POST /api/users/:id/unlock; on success → toast + invalidate the
 * `['user', id]` query so the parent UserDetailPage re-renders with the
 * updated state (which then HIDES this button).
 */

export interface UnlockButtonProps {
  readonly user: UserDetail;
  /** Caller's permission set — pass through from `useAuth().permissions`. */
  readonly permissions: readonly string[];
}

function isLocked(user: UserDetail): boolean {
  if (user.lockedUntil === null) return false;
  const t = new Date(user.lockedUntil).getTime();
  return Number.isFinite(t) && t > Date.now();
}

export function UnlockButton({ user, permissions }: UnlockButtonProps): JSX.Element | null {
  const queryClient = useQueryClient();

  const unlockMutation = useMutation<undefined, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      await unlockUserRequest(id);
      return undefined;
    },
    onSuccess: () => {
      toast.success('User unlocked');
      void queryClient.invalidateQueries({ queryKey: ['user', user.id] });
    },
    onError: (err) => {
      toast.error('Could not unlock user', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('users.unlock')) return null;
  if (!isLocked(user)) return null;

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => {
        unlockMutation.mutate({ id: user.id });
      }}
      disabled={unlockMutation.isPending}
      data-testid="user-unlock-button"
    >
      <LockKeyOpenIcon size={16} weight="bold" />
      {unlockMutation.isPending ? 'Unlocking…' : 'Unlock'}
    </Button>
  );
}
